import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteCompleteEvent } from 'primereact/autocomplete'
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown'
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect'
import ActiveFilters, { ActiveFilterChip } from '../../../components/novarium/ActiveFilters'
import MoreFiltersToggle from '../../../components/novarium/MoreFiltersToggle'
import PeriodField from '../../../components/novarium/PeriodField'
import { VIRTUAL_PANEL_CLASS, VIRTUAL_SCROLL } from '../../../components/novarium/virtualOptions'
import {
  OperatingRoomAgenda,
  OperatingRoomNomenclatorDTO,
  OperatingRoomOptionDTO,
  OperatingRoomOptionsDTO,
  OperatingRoomUrgency,
} from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { isRequestCanceled, searchOperatingRoomNomenclators } from '../../../services/OperatingRoomIndicatorsController'
import {
  activeFilterCount,
  NO_SELECT_FILTERS,
  OperatingRoomFilterField,
  OperatingRoomSelectFilters,
} from '../operatingRoomFilterModel'
import { OperatingRoomFiltersData } from '../useOperatingRoomFilters'

/** Valor de la opción «Todos»: el combo no admite `null` como valor seleccionado. */
const ALL = 'all'

const MORE_FILTERS_ID = 'operating-room-more-filters'

/** Los combos de «Más filtros» que eligen un id; los demás tienen su propio control. */
type ListField = 'surgeryTypeId' | 'surgeonId' | 'financierId' | 'anesthesiaId'

interface SelectOption {
  value: string
  label: string
}

/** Un combo de «Más filtros»: sus opciones (valores de texto, `ALL` es «Todos») y cómo se lee y se escribe en los filtros. */
interface ListSpec {
  field: ListField
  labelId: string
  options: SelectOption[]
  /** Lista larga: con buscador y scroll virtual. */
  long?: boolean
}

/** Mínimo de caracteres para buscar un código de nomenclador (el backend rechaza menos). */
const NOMENCLATOR_MIN_CHARS = 2

interface OperatingRoomFiltersProps {
  data: OperatingRoomFiltersData
  /** Opciones de los combos; `null` mientras no llegaron. */
  options: OperatingRoomOptionsDTO | null
}

const asText = (value: number | null): string => (value === null ? ALL : String(value))

const AGENDA_VALUES: OperatingRoomAgenda[] = ['ALL', 'SCHEDULED', 'OUT_OF_AGENDA']
const URGENCY_VALUES: OperatingRoomUrgency[] = ['ALL', 'URGENT', 'NOT_URGENT']

/** Búsqueda remota de un código de nomenclador: se piden las sugerencias al escribir y cada pedido cancela el anterior. */
function NomenclatorField({
  selected, onSelect,
}: { selected: OperatingRoomNomenclatorDTO | null; onSelect: (nomenclator: OperatingRoomNomenclatorDTO | null) => void }) {
  const intl = useIntl()
  const [query, setQuery] = useState<string | OperatingRoomNomenclatorDTO>(selected ?? '')
  const [suggestions, setSuggestions] = useState<OperatingRoomNomenclatorDTO[]>([])
  const controllerRef = useRef<AbortController | null>(null)

  // Un chip quitado (o «Quitar todos») vacía el campo
  useEffect(() => {
    setQuery((current) => {
      if (selected === null) return typeof current === 'string' ? current : ''
      return selected
    })
  }, [selected])

  useEffect(() => () => controllerRef.current?.abort(), [])

  const complete = useCallback((event: AutoCompleteCompleteEvent) => {
    controllerRef.current?.abort()
    const text = event.query.trim()
    if (text.length < NOMENCLATOR_MIN_CHARS) {
      setSuggestions([])
      return
    }
    const controller = new AbortController()
    controllerRef.current = controller
    searchOperatingRoomNomenclators(text, controller.signal)
      .then((found) => { if (!controller.signal.aborted) setSuggestions(found) })
      .catch((error: unknown) => {
        if (!controller.signal.aborted && !isRequestCanceled(error)) setSuggestions([])
      })
  }, [])

  const change = (event: AutoCompleteChangeEvent) => {
    const value = event.value as string | OperatingRoomNomenclatorDTO | null
    if (value === null || value === '') {
      setQuery('')
      if (selected !== null) onSelect(null)
      return
    }
    setQuery(value)
    if (typeof value !== 'string') onSelect(value)
  }

  return (
    <AutoComplete
      inputId="operating-room-nomenclatorId"
      value={query}
      suggestions={suggestions}
      completeMethod={complete}
      field="name"
      delay={300}
      minLength={NOMENCLATOR_MIN_CHARS}
      forceSelection
      emptyMessage={intl.formatMessage({ id: 'OperatingRoomIndicatorsFilterNoMatches' })}
      panelClassName="nv-scope"
      onChange={change}
    />
  )
}

/**
 * Filtros de quirófano, todos al instante (no hay «Consultar»). A la vista, el «Período» (un selector con las fechas solo en
 * «Personalizado») y «Más filtros» (quirófanos, tipo de cirugía, cirujano, código de nomenclador con búsqueda remota,
 * financiador, anestesia, agenda y urgencia); debajo, los chips con lo aplicado y «Quitar todos». «Agenda» (programadas o fuera de
 * agenda) y «Urgencia» son dos filtros separados e independientes.
 */
export default function OperatingRoomFilters({ data, options }: OperatingRoomFiltersProps) {
  const intl = useIntl()
  const { filters, errors, period, rangeText, changeFilter, patchFilters, changeDate, commitDates, changePeriod } = data

  const optionsReady = options !== null
  // Sin la acción 46 no hay nada del cirujano en la pantalla: tampoco el filtro (hasta saberlo, no se ofrece)
  const showSurgeon = options?.canGroupBySurgeon === true
  const all = intl.formatMessage({ id: 'OperatingRoomIndicatorsFilterAll' })

  const specs = useMemo<ListSpec[]>(() => {
    const byId = (list: OperatingRoomOptionDTO[] | undefined): SelectOption[] => [
      { value: ALL, label: all },
      ...(list ?? []).map((option) => ({ value: String(option.id), label: option.name })),
    ]
    return [
      { field: 'surgeryTypeId', labelId: 'OperatingRoomIndicatorsFilterSurgeryType', options: byId(options?.surgeryTypes) },
      ...(showSurgeon
        ? [{ field: 'surgeonId', labelId: 'OperatingRoomIndicatorsFilterSurgeon', options: byId(options?.surgeons), long: true } as const]
        : []),
      { field: 'financierId', labelId: 'OperatingRoomIndicatorsFilterFinancier', options: byId(options?.financiers), long: true },
      { field: 'anesthesiaId', labelId: 'OperatingRoomIndicatorsFilterAnesthesia', options: byId(options?.anesthesias) },
    ]
  }, [options, all, showSurgeon])

  const agendaOptions = useMemo<{ value: OperatingRoomAgenda; label: string }[]>(() => AGENDA_VALUES.map((value) => ({
    value,
    label: intl.formatMessage({ id: `OperatingRoomIndicatorsAgenda_${value}` }),
  })), [intl])
  const urgencyOptions = useMemo<{ value: OperatingRoomUrgency; label: string }[]>(() => URGENCY_VALUES.map((value) => ({
    value,
    label: intl.formatMessage({ id: `OperatingRoomIndicatorsUrgency_${value}` }),
  })), [intl])

  const dateErrors = useMemo(() => ({
    from: errors.from === undefined ? undefined : intl.formatMessage({ id: 'OperatingRoomIndicatorsDateRequiredError' }),
    to: errors.to === undefined
      ? undefined
      : intl.formatMessage({ id: errors.to === 'order' ? 'OperatingRoomIndicatorsDateOrderError' : 'OperatingRoomIndicatorsDateRequiredError' }),
  }), [errors.from, errors.to, intl])

  const moreActive = activeFilterCount(filters)
  // «Más filtros» se abre solo cuando pasa a haber alguno aplicado (también si lo activa una fila del agrupado); después se puede cerrar
  const [moreOpen, setMoreOpen] = useState(moreActive > 0)
  useEffect(() => {
    if (moreActive > 0) setMoreOpen(true)
  }, [moreActive])

  const roomOptions = useMemo(() => (options?.rooms ?? []).map((room) => ({ value: room.id, label: room.name })), [options])

  /** Un chip por filtro elegido, «Filtro: valor»; las opciones todavía sin cargar se nombran por su valor. */
  const chips = useMemo<ActiveFilterChip<OperatingRoomFilterField>[]>(() => {
    const chip = (key: OperatingRoomFilterField, labelId: string, name: string): ActiveFilterChip<OperatingRoomFilterField> => ({
      key,
      label: intl.formatMessage({ id: 'OperatingRoomIndicatorsChip' }, { filter: intl.formatMessage({ id: labelId }), name }),
    })
    const unknown = (id: number) => intl.formatMessage({ id: 'OperatingRoomIndicatorsChipUnknown' }, { id })
    const list: ActiveFilterChip<OperatingRoomFilterField>[] = []
    if (filters.roomIds.length > 0) {
      const names = filters.roomIds.map((id) => roomOptions.find((room) => room.value === id)?.label ?? unknown(id))
      list.push(chip('roomIds', 'OperatingRoomIndicatorsFilterRooms', names.join(', ')))
    }
    specs.forEach((spec) => {
      const value = filters[spec.field]
      if (value === null) return
      const name = spec.options.find((option) => option.value === String(value))?.label ?? unknown(value)
      list.push(chip(spec.field, spec.labelId, name))
    })
    if (filters.nomenclator) list.push(chip('nomenclator', 'OperatingRoomIndicatorsFilterNomenclator', filters.nomenclator.name))
    if (filters.agenda !== 'ALL') {
      list.push(chip('agenda', 'OperatingRoomIndicatorsFilterAgenda', intl.formatMessage({ id: `OperatingRoomIndicatorsAgenda_${filters.agenda}` })))
    }
    if (filters.agendaBlock) list.push(chip('agendaBlock', 'OperatingRoomIndicatorsFilterAgendaBlock', filters.agendaBlock.name))
    if (filters.urgency !== 'ALL') {
      list.push(chip('urgency', 'OperatingRoomIndicatorsFilterUrgency', intl.formatMessage({ id: `OperatingRoomIndicatorsUrgency_${filters.urgency}` })))
    }
    return list
  }, [specs, filters, roomOptions, intl])

  const onSelect = (field: ListField) => (event: DropdownChangeEvent) => {
    const value = event.value as string
    changeFilter({ [field]: value === ALL ? null : Number(value) } as Partial<OperatingRoomSelectFilters>)
  }

  const removeChip = (field: OperatingRoomFilterField) => patchFilters({ [field]: NO_SELECT_FILTERS[field] } as Partial<OperatingRoomSelectFilters>)

  return (
    <section className="nv-section" role="search" aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsFilters' })}>
      <div className="nv-form-row">
        <PeriodField
          idPrefix="operating-room"
          period={period}
          rangeText={rangeText}
          from={filters.from}
          to={filters.to}
          errors={dateErrors}
          onDateChange={changeDate}
          onDatesCommit={commitDates}
          onPeriodChange={changePeriod}
        />
        <MoreFiltersToggle
          open={moreOpen}
          active={moreActive}
          controls={MORE_FILTERS_ID}
          onToggle={() => setMoreOpen((open) => !open)}
        />
      </div>

      {moreOpen && (
        <div id={MORE_FILTERS_ID} className="nv-form-row">
          <div className="nv-field nv-w-m">
            <label htmlFor="operating-room-roomIds">{intl.formatMessage({ id: 'OperatingRoomIndicatorsFilterRooms' })}</label>
            <MultiSelect
              inputId="operating-room-roomIds"
              value={filters.roomIds}
              options={roomOptions}
              optionLabel="label"
              optionValue="value"
              display="comma"
              maxSelectedLabels={2}
              selectedItemsLabel={intl.formatMessage({ id: 'OperatingRoomIndicatorsFilterRoomsSelected' })}
              placeholder={all}
              showSelectAll={false}
              disabled={!optionsReady}
              panelClassName="nv-scope"
              onChange={(event: MultiSelectChangeEvent) => changeFilter({ roomIds: event.value as number[] })}
            />
          </div>
          {specs.map((spec) => {
            const inputId = `operating-room-${spec.field}`
            return (
              <div key={spec.field} className="nv-field nv-w-m">
                <label htmlFor={inputId}>{intl.formatMessage({ id: spec.labelId })}</label>
                <Dropdown
                  inputId={inputId}
                  value={asText(filters[spec.field])}
                  options={spec.options}
                  optionLabel="label"
                  optionValue="value"
                  disabled={!optionsReady}
                  {...(spec.long
                    ? {
                      filter: true,
                      filterBy: 'label',
                      virtualScrollerOptions: VIRTUAL_SCROLL,
                      panelClassName: `nv-scope ${VIRTUAL_PANEL_CLASS}`,
                      emptyFilterMessage: intl.formatMessage({ id: 'OperatingRoomIndicatorsFilterNoMatches' }),
                    }
                    : { panelClassName: 'nv-scope' })}
                  onChange={onSelect(spec.field)}
                />
              </div>
            )
          })}
          <div className="nv-field nv-w-m">
            <label htmlFor="operating-room-nomenclatorId">{intl.formatMessage({ id: 'OperatingRoomIndicatorsFilterNomenclator' })}</label>
            <NomenclatorField selected={filters.nomenclator} onSelect={(nomenclator) => changeFilter({ nomenclator })} />
          </div>
          <div className="nv-field nv-w-s">
            <label htmlFor="operating-room-agenda">{intl.formatMessage({ id: 'OperatingRoomIndicatorsFilterAgenda' })}</label>
            <Dropdown
              inputId="operating-room-agenda"
              value={filters.agenda}
              options={agendaOptions}
              optionLabel="label"
              optionValue="value"
              panelClassName="nv-scope"
              onChange={(event: DropdownChangeEvent) => changeFilter({ agenda: event.value as OperatingRoomAgenda })}
            />
          </div>
          <div className="nv-field nv-w-s">
            <label htmlFor="operating-room-urgency">{intl.formatMessage({ id: 'OperatingRoomIndicatorsFilterUrgency' })}</label>
            <Dropdown
              inputId="operating-room-urgency"
              value={filters.urgency}
              options={urgencyOptions}
              optionLabel="label"
              optionValue="value"
              panelClassName="nv-scope"
              onChange={(event: DropdownChangeEvent) => changeFilter({ urgency: event.value as OperatingRoomUrgency })}
            />
          </div>
        </div>
      )}

      <ActiveFilters
        chips={chips}
        onRemove={removeChip}
        onClear={() => patchFilters(NO_SELECT_FILTERS)}
      />
    </section>
  )
}
